// client/src/pages/Login.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, EyeOff, Shield, Zap, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { LDGrowthIcon } from '@/components/ui/LDGrowthIcon';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TypewriterText } from '@/components/ui/TypewriterText';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { loginRateLimiter, getRateLimitKey, formatBlockedTime } from '@/lib/utils/rate-limiter';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The branding column is display:none below lg, but React would still mount
  // the typewriter and mutate the DOM every 80 ms on phones. Only mount it when
  // it can actually be seen.
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Refs for proper focus management
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the email input only on desktop, where a keyboard is already
    // there. On phones the programmatic focus() was the single most expensive
    // app-level call on first paint (~100-150 ms of forced layout at 8x CPU
    // throttle), and iOS ignores it anyway: no keyboard appears without a tap.
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    emailRef.current?.focus();
  }, []);

  // Enhanced validation with consistent 8-character minimum
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError(t('auth.emailRequired'));
      return false;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError(t('auth.invalidEmail'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError(t('auth.passwordRequired'));
      return false;
    }
    // Standardized to 8 characters minimum across all auth pages
    if (password.length < 8) {
      setPasswordError(t('auth.passwordMinLength', 'Password must be at least 8 characters long'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  // Real-time form validation
  useEffect(() => {
    const isEmailValid = !!email && validateEmail(email);
    const isPasswordValid = !!password && validatePassword(password);
    setIsFormValid(isEmailValid && isPasswordValid);
  }, [email, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email.trim());
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      // Focus on first invalid field
      if (!isEmailValid && emailRef.current) {
        emailRef.current.focus();
      } else if (!isPasswordValid && passwordRef.current) {
        passwordRef.current.focus();
      }
      return;
    }

    // Check rate limiting
    const rateLimitKey = getRateLimitKey(email.trim());
    if (!loginRateLimiter.isAllowed(rateLimitKey)) {
      const blockedTime = loginRateLimiter.getBlockedTime(rateLimitKey);
      const timeString = formatBlockedTime(blockedTime);
      setError(`Too many failed attempts. Please try again in ${timeString}.`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await login(email.trim(), password, rememberMe);

      // Record successful login
      loginRateLimiter.recordAttempt(rateLimitKey, true);

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email.trim());
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      navigate('/');
    } catch (error: any) {
      const _castErr = error as { response?: { status?: number; data?: { message?: string; [k: string]: unknown }; }; message?: string; code?: string; config?: any; };
      // Record failed attempt
      loginRateLimiter.recordAttempt(rateLimitKey, false);

      const remainingAttempts = loginRateLimiter.getRemainingAttempts(rateLimitKey);
      let errorMessage = _castErr.response?.data?.message || t('auth.loginError');

      if (remainingAttempts > 0 && remainingAttempts <= 2) {
        errorMessage += ` (${remainingAttempts} attempt${remainingAttempts === 1 ? '' : 's'} remaining)`;
      }

      setError(errorMessage);

      // Focus password field for retry
      if (passwordRef.current) {
        passwordRef.current.focus();
        passwordRef.current.select();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
      // Focus password field if email is pre-filled
      if (passwordRef.current) {
        setTimeout(() => passwordRef.current?.focus(), 100);
      }
    }
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !isLoading) {
      handleSubmit(e as any);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--ld-neutral-50)] via-white to-[var(--ld-primary-50)] flex items-center justify-center p-4 ld-bg-pattern relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--ld-primary)] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--ld-primary)] opacity-3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-20 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Branding & Features */}
        <div className="hidden lg:block space-y-8 ld-animate-fade-in">
          {/* Brand Header */}
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <LDGrowthIcon size={64} className="ld-shadow-glow ld-animate-float" />
              <div>
                <h1 className="text-5xl font-bold ld-text-gradient">
                  LD Growth
                </h1>
                <p className="text-[var(--ld-neutral-600)] text-xl font-medium">
                  {isDesktop ? (
                    <TypewriterText
                      texts={[
                        'Leadership Development Platform',
                        'Empowering Team Growth',
                        'Performance Management Suite',
                        'Building Future Leaders'
                      ]}
                      speed={80}
                      deleteSpeed={40}
                      pauseDuration={3000}
                    />
                  ) : (
                    'Leadership Development Platform'
                  )}
                </p>
              </div>
            </div>
            <p className="text-[var(--ld-neutral-700)] text-lg leading-relaxed">
              Transform your team's potential with our comprehensive leadership development and performance management platform.
            </p>
          </div>

          {/* Feature Highlights */}
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-white/60 rounded-xl border border-white/20">
              <div className="w-12 h-12 bg-[var(--ld-primary)] rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--ld-neutral-800)]">Team Development</h3>
                <p className="text-[var(--ld-neutral-600)] text-sm">Comprehensive leadership roadmaps and growth tracking</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/60 rounded-xl border border-white/20">
              <div className="w-12 h-12 bg-[var(--ld-primary)] rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--ld-neutral-800)]">Performance Analytics</h3>
                <p className="text-[var(--ld-neutral-600)] text-sm">Real-time insights and progress monitoring</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-white/60 rounded-xl border border-white/20">
              <div className="w-12 h-12 bg-[var(--ld-primary)] rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-[var(--ld-neutral-800)]">Enterprise Security</h3>
                <p className="text-[var(--ld-neutral-600)] text-sm">Bank-level security with advanced authentication</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md mx-auto lg:mx-0 ld-animate-slide-up">
          {/* Mobile Brand Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <LDGrowthIcon size={48} className="ld-animate-float" />
              <h1 className="text-3xl font-bold ld-text-gradient">LD Growth</h1>
            </div>
            <p className="text-[var(--ld-neutral-600)] font-medium">Leadership Development Platform</p>
          </div>

          <Card className="bg-white/80 border-0 shadow-2xl ld-shadow-glow">
            <CardContent className="p-8">
              {/* Form Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[var(--ld-neutral-800)] mb-2">
                  {t('auth.signIn')}
                </h2>
                <p className="text-[var(--ld-neutral-600)]">
                  Welcome back! Please sign in to your account.
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-[var(--ld-error-light)] border border-[var(--ld-error)]/20 text-[var(--ld-error)] rounded-xl text-sm font-medium ld-animate-scale-in">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[var(--ld-error)] rounded-full flex-shrink-0"></div>
                    {error}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" onKeyDown={handleKeyDown}>
                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-[var(--ld-neutral-700)]">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <input
                      ref={emailRef}
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      className={`w-full h-14 px-4 pr-12 rounded-xl border-2 transition-all duration-300 bg-white/50 ${
                        emailError
                          ? 'border-[var(--ld-error)] focus:border-[var(--ld-error)] focus:ring-4 focus:ring-[var(--ld-error)]/20'
                          : email && !emailError
                          ? 'border-[var(--ld-success)] focus:border-[var(--ld-success)] focus:ring-4 focus:ring-[var(--ld-success)]/20'
                          : 'border-[var(--ld-neutral-200)] focus:border-[var(--ld-primary)] focus:ring-4 focus:ring-[var(--ld-primary)]/20'
                      } focus:outline-none placeholder:text-[var(--ld-neutral-400)] text-[var(--ld-neutral-800)] font-medium`}
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (e.target.value) validateEmail(e.target.value);
                      }}
                      onBlur={() => email && validateEmail(email)}
                    />
                    {email && !emailError && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <div className="w-5 h-5 bg-[var(--ld-success)] rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  {emailError && (
                    <p className="text-sm text-[var(--ld-error)] font-medium flex items-center gap-2 ld-animate-scale-in">
                      <span className="w-1 h-1 bg-[var(--ld-error)] rounded-full"></span>
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-semibold text-[var(--ld-neutral-700)]">
                    {t('auth.password')}
                  </label>
                  <div className="relative">
                    <input
                      ref={passwordRef}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      className={`w-full h-14 px-4 pr-12 rounded-xl border-2 transition-all duration-300 bg-white/50 ${
                        passwordError
                          ? 'border-[var(--ld-error)] focus:border-[var(--ld-error)] focus:ring-4 focus:ring-[var(--ld-error)]/20'
                          : password && !passwordError
                          ? 'border-[var(--ld-success)] focus:border-[var(--ld-success)] focus:ring-4 focus:ring-[var(--ld-success)]/20'
                          : 'border-[var(--ld-neutral-200)] focus:border-[var(--ld-primary)] focus:ring-4 focus:ring-[var(--ld-primary)]/20'
                      } focus:outline-none placeholder:text-[var(--ld-neutral-400)] text-[var(--ld-neutral-800)] font-medium`}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (e.target.value) validatePassword(e.target.value);
                      }}
                      onBlur={() => password && validatePassword(password)}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ld-neutral-500)] hover:text-[var(--ld-neutral-700)] transition-colors duration-200 p-1"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {passwordError && (
                    <p className="text-sm text-[var(--ld-error)] font-medium flex items-center gap-2 ld-animate-scale-in">
                      <span className="w-1 h-1 bg-[var(--ld-error)] rounded-full"></span>
                      {passwordError}
                    </p>
                  )}
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                      className="border-2 border-[var(--ld-neutral-300)] data-[state=checked]:bg-[var(--ld-primary)] data-[state=checked]:border-[var(--ld-primary)]"
                    />
                    <label htmlFor="remember-me" className="text-sm font-medium text-[var(--ld-neutral-700)] cursor-pointer">
                      {t('auth.rememberMe')}
                    </label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-semibold text-[var(--ld-primary)] hover:text-[var(--ld-primary-dark)] transition-colors duration-200"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  className={`w-full h-14 rounded-xl font-semibold text-base transition-all duration-300 ${
                    isFormValid && !isLoading
                      ? 'bg-[var(--ld-primary)] hover:bg-[var(--ld-primary-dark)] text-white shadow-lg hover:shadow-xl ld-shadow-glow transform hover:scale-[1.02]'
                      : 'bg-[var(--ld-neutral-200)] text-[var(--ld-neutral-500)] cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-3">
                      <LoadingSpinner size="sm" color="white" />
                      <span>{t('auth.signingIn')}</span>
                    </div>
                  ) : (
                    t('auth.signIn')
                  )}
                </Button>

                {/* Register Link */}
                <div className="text-center pt-4 border-t border-[var(--ld-neutral-200)]">
                  <p className="text-[var(--ld-neutral-600)] text-sm">
                    {t('auth.noAccount')}{' '}
                    <Link
                      to="/register"
                      className="font-semibold text-[var(--ld-primary)] hover:text-[var(--ld-primary-dark)] transition-colors duration-200"
                    >
                      {t('auth.registerCta')}
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Security Badge */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 rounded-full border border-white/20">
              <Shield className="w-4 h-4 text-[var(--ld-success)]" />
              <span className="text-sm font-medium text-[var(--ld-neutral-700)]">
                Enterprise-grade security
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}